import { useEffect, useState, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Storefront, Plus, PencilSimple, Trash, Package, Receipt, MagnifyingGlass,
  ArrowRight, FloppyDisk, X,
} from "@phosphor-icons/react";
import StockPartCombobox from "@/components/StockPartCombobox";
import { fmtDateTime as fmtDate } from "@/lib/dates";

const TABS = [
  { key: "directory", label: "Directory", icon: Storefront },
  { key: "receive",   label: "Receive Shipment", icon: Package },
  { key: "history",   label: "Purchase History", icon: Receipt },
];

export default function SuppliersPage() {
  const { user } = useAuth();
  const isManager = user.role === "manager";
  const [tab, setTab] = useState("directory");

  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, editing: null });

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/suppliers");
      setSuppliers(res.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (s) => {
    if (!window.confirm(`Remove supplier "${s.name}"? If any shipments exist, the record is kept but marked inactive.`)) return;
    try {
      const res = await api.delete(`/suppliers/${s.id}`);
      toast.success(res.data.soft_deleted ? `Deactivated (kept ${res.data.shipments} historical shipment${res.data.shipments === 1 ? "" : "s"})` : "Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="suppliers-page">
      <div className="border-b border-neutral-200 px-10 py-8 bg-white sticky top-0 z-10">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="label-eyebrow">Supply Chain</div>
            <h1 className="font-display text-4xl tracking-tight mt-1">Suppliers</h1>
            <div className="text-xs text-neutral-500 mt-1">Directory · receive shipments · purchase history — Manager only</div>
          </div>
        </div>

        <div className="mt-6 flex border border-neutral-300" data-testid="suppliers-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                data-testid={`tab-${t.key}`}
                className={`h-11 px-4 text-xs font-mono uppercase tracking-wider border-r last:border-r-0 border-neutral-300 flex-1 inline-flex items-center justify-center gap-2 ${
                  active ? "bg-neutral-900 text-white" : "bg-white hover:bg-neutral-50"
                }`}
              >
                <Icon size={13} weight="bold" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-10">
        {tab === "directory" && (
          <DirectoryTab
            suppliers={suppliers}
            loading={loading}
            isManager={isManager}
            onAdd={() => setDialog({ open: true, editing: null })}
            onEdit={(s) => setDialog({ open: true, editing: s })}
            onDelete={del}
          />
        )}
        {tab === "receive" && (
          <ReceiveTab
            suppliers={suppliers.filter((s) => s.active !== false)}
            isManager={isManager}
            onSaved={load}
          />
        )}
        {tab === "history" && (
          <HistoryTab suppliers={suppliers} isManager={isManager} />
        )}
      </div>

      <SupplierDialog
        open={dialog.open}
        editing={dialog.editing}
        onClose={() => setDialog({ open: false, editing: null })}
        onSaved={() => { setDialog({ open: false, editing: null }); load(); }}
      />
    </div>
  );
}


function DirectoryTab({ suppliers, loading, isManager, onAdd, onEdit, onDelete }) {
  const [q, setQ] = useState("");
  const filtered = suppliers.filter((s) => {
    const t = q.trim().toLowerCase();
    if (!t) return true;
    return [s.name, s.contact_person, s.email, s.phone, s.address].some((v) => (v || "").toLowerCase().includes(t));
  });

  return (
    <div className="space-y-4" data-testid="directory-tab">
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input
            placeholder="Filter by name, contact, email, phone…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-10 rounded-none border-neutral-400"
            data-testid="directory-filter"
          />
        </div>
        {isManager && (
          <Button
            onClick={onAdd}
            data-testid="add-supplier-btn"
            className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800 h-10"
          >
            <Plus size={14} weight="bold" className="mr-1" /> Add supplier
          </Button>
        )}
      </div>

      <div className="border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="suppliers-table">
          <thead className="border-b border-neutral-200 bg-neutral-50">
            <tr className="text-left">
              <th className="px-4 py-3 label-eyebrow">Name</th>
              <th className="px-4 py-3 label-eyebrow">Contact</th>
              <th className="px-4 py-3 label-eyebrow">Phone</th>
              <th className="px-4 py-3 label-eyebrow">Email</th>
              <th className="px-4 py-3 label-eyebrow">Status</th>
              {isManager && <th className="px-4 py-3 label-eyebrow">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-4 py-10 text-center text-neutral-400 text-xs">Loading…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-neutral-400">
                {suppliers.length === 0
                  ? (isManager ? "No suppliers yet. Click 'Add supplier'." : "No suppliers configured.")
                  : "No matches."}
              </td></tr>
            )}
            {!loading && filtered.map((s) => (
              <tr key={s.id} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50/60" data-testid={`supplier-row-${s.id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium">{s.name}</div>
                  {s.address && <div className="text-[10px] text-neutral-500 mt-0.5">{s.address}</div>}
                </td>
                <td className="px-4 py-3 text-neutral-700">{s.contact_person || <span className="text-neutral-400">—</span>}</td>
                <td className="px-4 py-3 font-mono text-xs">{s.phone || <span className="text-neutral-400">—</span>}</td>
                <td className="px-4 py-3 font-mono text-xs">{s.email || <span className="text-neutral-400">—</span>}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border ${s.active ? "border-emerald-500 bg-emerald-50 text-emerald-900" : "border-neutral-400 bg-neutral-100 text-neutral-600"}`}>
                    {s.active ? "Active" : "Inactive"}
                  </span>
                </td>
                {isManager && (
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button
                        onClick={() => onEdit(s)}
                        className="p-1.5 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white"
                        title="Edit"
                        data-testid={`edit-supplier-${s.id}`}
                      >
                        <PencilSimple size={13} weight="bold" />
                      </button>
                      <button
                        onClick={() => onDelete(s)}
                        className="p-1.5 border border-neutral-300 hover:border-red-600 hover:bg-red-600 hover:text-white"
                        title="Remove"
                        data-testid={`delete-supplier-${s.id}`}
                      >
                        <Trash size={13} weight="bold" />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function SupplierDialog({ open, editing, onClose, onSaved }) {
  const isEdit = !!editing;
  const [form, setForm] = useState({ name: "", contact_person: "", phone: "", email: "", address: "", notes: "", active: true });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setForm({
        name: editing?.name || "",
        contact_person: editing?.contact_person || "",
        phone: editing?.phone || "",
        email: editing?.email || "",
        address: editing?.address || "",
        notes: editing?.notes || "",
        active: editing?.active !== false,
      });
    }
  }, [open, editing]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      const payload = { ...form, name: form.name.trim() };
      if (isEdit) await api.patch(`/suppliers/${editing.id}`, payload);
      else await api.post("/suppliers", payload);
      toast.success(isEdit ? "Supplier updated" : "Supplier added");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg rounded-none border-neutral-900" data-testid="supplier-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">
            {isEdit ? `Edit — ${editing?.name}` : "Add supplier"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Name *</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-none border-neutral-400" data-testid="supplier-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Contact person</Label>
              <Input value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} className="rounded-none border-neutral-400" data-testid="supplier-contact" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Phone</Label>
              <Input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="rounded-none border-neutral-400 font-mono" data-testid="supplier-phone" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Email</Label>
            <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="rounded-none border-neutral-400 font-mono" data-testid="supplier-email" />
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Address</Label>
            <Textarea rows={2} value={form.address} onChange={(e) => set("address", e.target.value)} className="rounded-none border-neutral-400" data-testid="supplier-address" />
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="rounded-none border-neutral-400" data-testid="supplier-notes" />
          </div>
          <label className="flex items-center gap-2 pt-1 cursor-pointer select-none">
            <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} className="accent-neutral-900" data-testid="supplier-active" />
            <span className="text-sm">Active — available in Receive Shipment</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="supplier-save" className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800">
            <FloppyDisk size={14} weight="bold" className="mr-1" />
            {busy ? "Saving…" : (isEdit ? "Save changes" : "Add supplier")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ReceiveTab({ suppliers, isManager, onSaved }) {
  const [supplierId, setSupplierId] = useState("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const minDate = (() => {
    const d = new Date(); d.setDate(d.getDate() - 7);
    return d.toISOString().slice(0, 10);
  })();
  const [receivedDate, setReceivedDate] = useState(today);
  const emptyLine = () => ({
    _key: Math.random().toString(36).slice(2),
    stock_code: null, name: "", group: null, uom: null,
    qty: "", unit_cost: "",
  });
  const [lines, setLines] = useState([emptyLine()]);

  const setLine = (key, patch) =>
    setLines((arr) => arr.map((l) => (l._key === key ? { ...l, ...patch } : l)));
  const addLine = () => setLines((arr) => [...arr, emptyLine()]);
  const removeLine = (key) =>
    setLines((arr) => (arr.length <= 1 ? arr : arr.filter((l) => l._key !== key)));

  const totals = useMemo(() => {
    let units = 0, value = 0, anyCost = false;
    for (const l of lines) {
      const q = parseInt(l.qty, 10);
      if (!q || isNaN(q)) continue;
      units += q;
      const u = parseFloat(l.unit_cost);
      if (!isNaN(u)) { value += q * u; anyCost = true; }
    }
    return { units, value: anyCost ? value : null };
  }, [lines]);

  const reset = () => {
    setLines([emptyLine()]);
    setInvoiceNo("");
    setNotes("");
    setReceivedDate(today);
  };

  const submit = async () => {
    if (!isManager) return toast.error("Only a Manager can receive shipments");
    if (!supplierId) return toast.error("Pick a supplier");
    const cleaned = [];
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i];
      if (!l.name.trim()) return toast.error(`Line ${i + 1}: pick a part`);
      const q = parseInt(l.qty, 10);
      if (!q || q <= 0) return toast.error(`Line ${i + 1}: quantity must be > 0`);
      const u = l.unit_cost === "" ? null : parseFloat(l.unit_cost);
      if (u !== null && (isNaN(u) || u < 0)) return toast.error(`Line ${i + 1}: unit cost is invalid`);
      cleaned.push({
        stock_code: l.stock_code || null,
        name: l.name,
        group: l.group || null,
        uom: l.uom || null,
        quantity: q,
        unit_cost: u,
      });
    }
    setBusy(true);
    try {
      const body = {
        supplier_id: supplierId,
        lines: cleaned,
        invoice_no: invoiceNo.trim() || null,
        notes: notes.trim() || null,
        received_at: receivedDate ? `${receivedDate}T00:00:00+00:00` : null,
      };
      const res = await api.post("/suppliers/receive-shipment", body);
      const nLines = cleaned.length;
      const totalUnits = cleaned.reduce((s, l) => s + l.quantity, 0);
      toast.success(
        `Shipment ${res.data.shipment_no} logged — ${totalUnits} units across ${nLines} line${nLines === 1 ? "" : "s"} added to stock`
      );
      reset();
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!isManager) {
    return (
      <div className="border border-neutral-200 bg-neutral-50 px-4 py-8 text-center text-sm text-neutral-500" data-testid="receive-locked">
        Only a Manager can receive shipments. Ask a manager to log this delivery.
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-4" data-testid="receive-tab">
      <div className="border border-neutral-200 bg-neutral-50 px-3 py-2 text-[11px] text-neutral-600">
        Add every part on the delivery invoice — one line each with its own quantity and unit cost.
        On save, all lines are logged as one shipment record and the master-list on-hand quantities are auto-increased.
      </div>

      <div className="space-y-1.5">
        <Label className="label-eyebrow">Supplier *</Label>
        <select
          value={supplierId}
          onChange={(e) => setSupplierId(e.target.value)}
          className="w-full h-10 border border-neutral-400 rounded-none px-3 bg-white text-sm"
          data-testid="receive-supplier"
        >
          <option value="">— Select a supplier —</option>
          {suppliers.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </div>

      <div className="border border-neutral-300 bg-white">
        <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-neutral-200 bg-neutral-50 label-eyebrow text-[10px]">
          <div className="col-span-6">Part</div>
          <div className="col-span-2 text-right">Qty *</div>
          <div className="col-span-2 text-right">Unit cost (MYR)</div>
          <div className="col-span-1 text-right">Line total</div>
          <div className="col-span-1"></div>
        </div>
        <div className="divide-y divide-neutral-100">
          {lines.map((l, idx) => {
            const q = parseInt(l.qty, 10);
            const u = parseFloat(l.unit_cost);
            const lineTotal = (!isNaN(q) && !isNaN(u)) ? q * u : null;
            return (
              <div
                key={l._key}
                className="grid grid-cols-12 gap-2 px-3 py-2 items-start"
                data-testid={`receive-line-${idx}`}
              >
                <div className="col-span-6">
                  <StockPartCombobox
                    value={{
                      stock_code: l.stock_code,
                      name: l.name,
                      group: l.group,
                      uom: l.uom,
                    }}
                    onChange={(picked) => setLine(l._key, {
                      stock_code: picked.stock_code || null,
                      name: picked.name || "",
                      group: picked.group || null,
                      uom: picked.uom || null,
                    })}
                    testid={`receive-line-${idx}-part`}
                    placeholder="Search master list…"
                  />
                  {l.stock_code && (
                    <div className="text-[10px] font-mono text-neutral-500 mt-1">
                      {l.stock_code}{l.uom ? ` · ${l.uom}` : ""}{l.group ? ` · ${l.group}` : ""}
                    </div>
                  )}
                </div>
                <div className="col-span-2">
                  <Input
                    type="number" min={1}
                    value={l.qty}
                    onChange={(e) => setLine(l._key, { qty: e.target.value })}
                    className="rounded-none border-neutral-400 font-mono text-right h-9"
                    data-testid={`receive-line-${idx}-qty`}
                  />
                </div>
                <div className="col-span-2">
                  <Input
                    type="number" step="0.01" min={0}
                    value={l.unit_cost}
                    onChange={(e) => setLine(l._key, { unit_cost: e.target.value })}
                    className="rounded-none border-neutral-400 font-mono text-right h-9"
                    data-testid={`receive-line-${idx}-cost`}
                    placeholder="—"
                  />
                </div>
                <div className="col-span-1 pt-1.5 text-right font-mono text-sm">
                  {lineTotal !== null
                    ? lineTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    : <span className="text-neutral-300">—</span>}
                </div>
                <div className="col-span-1 flex justify-end pt-0.5">
                  <button
                    type="button"
                    onClick={() => removeLine(l._key)}
                    disabled={lines.length <= 1}
                    className="p-1.5 border border-neutral-300 hover:border-red-600 hover:bg-red-600 hover:text-white disabled:opacity-30 disabled:hover:border-neutral-300 disabled:hover:bg-transparent disabled:hover:text-current"
                    title={lines.length <= 1 ? "At least one line required" : "Remove line"}
                    data-testid={`receive-line-${idx}-remove`}
                  >
                    <X size={13} weight="bold" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        <div className="border-t border-neutral-200 px-3 py-2 flex items-center justify-between bg-neutral-50">
          <button
            type="button"
            onClick={addLine}
            className="text-xs font-mono uppercase tracking-wider inline-flex items-center gap-1.5 hover:text-neutral-900 text-neutral-700"
            data-testid="receive-add-line"
          >
            <Plus size={13} weight="bold" /> Add another part
          </button>
          <div className="text-[11px] font-mono text-neutral-500">
            {lines.length} line{lines.length === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="border border-neutral-300 bg-white px-3 py-2 flex items-center justify-between" data-testid="receive-total-units">
          <span className="label-eyebrow">Total units</span>
          <span className="font-mono text-lg">{totals.units.toLocaleString()}</span>
        </div>
        <div className="border border-neutral-900 bg-neutral-900 text-white px-3 py-2 flex items-center justify-between" data-testid="receive-total-value">
          <span className="text-[10px] font-mono uppercase tracking-wider opacity-70">Total value (MYR)</span>
          <span className="font-mono text-lg">
            {totals.value !== null
              ? `RM ${totals.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : "—"}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Received Date</Label>
          <Input
            type="date"
            value={receivedDate}
            min={minDate}
            max={today}
            onChange={(e) => setReceivedDate(e.target.value)}
            className="rounded-none border-neutral-400 font-mono"
            data-testid="receive-date"
          />
          <div className="text-[10px] text-neutral-500 font-mono">Up to 7 days back</div>
        </div>
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Invoice # (optional)</Label>
          <Input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} className="rounded-none border-neutral-400 font-mono" data-testid="receive-invoice" />
        </div>
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Notes (optional)</Label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-none border-neutral-400" data-testid="receive-notes" />
        </div>
      </div>

      <div className="pt-2 flex items-center gap-2">
        <Button
          onClick={submit}
          disabled={busy || !supplierId}
          data-testid="receive-submit"
          className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800 h-11 px-5 disabled:opacity-40"
        >
          <ArrowRight size={16} weight="bold" className="mr-2" />
          {busy ? "Adding to stock…" : "Add shipment to Stock"}
        </Button>
        <Button
          onClick={reset}
          disabled={busy}
          variant="outline"
          className="rounded-none border-neutral-900 h-11 hover:bg-neutral-900 hover:text-white"
          data-testid="receive-reset"
        >
          Reset
        </Button>
      </div>
    </div>
  );
}


function HistoryTab({ suppliers, isManager }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [supplierId, setSupplierId] = useState("");
  const [poSearch, setPoSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (supplierId) params.supplier_id = supplierId;
      if (poSearch.trim()) params.q = poSearch.trim();
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo)   params.date_to   = new Date(dateTo + "T23:59:59").toISOString();
      const res = await api.get("/suppliers/shipments", { params });
      setRows(res.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [supplierId, dateFrom, dateTo]);
  // Debounce PO search so we don't fire a request per keystroke.
  useEffect(() => {
    const id = setTimeout(load, 250);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poSearch]);

  const totalUnits = rows.reduce((s, r) => s + (r.total_units || 0), 0);
  const totalValue = rows.reduce((s, r) => s + (r.total_value || 0), 0);

  return (
    <div className="space-y-4" data-testid="history-tab">
      <div className="grid grid-cols-6 gap-3">
        <div className="space-y-1.5 col-span-2">
          <Label className="label-eyebrow">Supplier</Label>
          <select
            value={supplierId}
            onChange={(e) => setSupplierId(e.target.value)}
            className="w-full h-10 border border-neutral-400 rounded-none px-3 bg-white text-sm"
            data-testid="history-supplier"
          >
            <option value="">All suppliers</option>
            {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div className="space-y-1.5 col-span-2">
          <Label className="label-eyebrow">PO / Invoice # lookup</Label>
          <div className="relative">
            <MagnifyingGlass size={13} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              value={poSearch}
              onChange={(e) => setPoSearch(e.target.value)}
              placeholder="PO123, SH000042…"
              className="h-10 pl-9 rounded-none border-neutral-400 font-mono"
              data-testid="history-po-search"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="label-eyebrow">From</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-none border-neutral-400 font-mono" data-testid="history-from" />
        </div>
        <div className="space-y-1.5">
          <Label className="label-eyebrow">To</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-none border-neutral-400 font-mono" data-testid="history-to" />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Shipments" value={rows.length} />
        <StatCard label="Units received" value={totalUnits.toLocaleString()} />
        <StatCard label="Total value (MYR)" value={`RM ${totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} mono />
      </div>

      <div className="border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="history-table">
          <thead className="border-b border-neutral-200 bg-neutral-50">
            <tr className="text-left">
              <th className="px-3 py-2 label-eyebrow">Shipment</th>
              <th className="px-3 py-2 label-eyebrow">Received</th>
              <th className="px-3 py-2 label-eyebrow">Supplier</th>
              <th className="px-3 py-2 label-eyebrow">Parts</th>
              <th className="px-3 py-2 label-eyebrow text-right">Units</th>
              <th className="px-3 py-2 label-eyebrow text-right">Total value (MYR)</th>
              <th className="px-3 py-2 label-eyebrow">PO / Invoice</th>
              <th className="px-3 py-2 label-eyebrow">By</th>
              {isManager && <th className="px-3 py-2 label-eyebrow text-right">Fix</th>}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={isManager ? 9 : 8} className="px-3 py-10 text-center text-neutral-400 text-xs">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={isManager ? 9 : 8} className="px-3 py-10 text-center text-neutral-400">
                {poSearch ? `No shipments matching '${poSearch}'.` : "No shipments logged for the selected filters."}
              </td></tr>
            )}
            {!loading && rows.map((r) => (
              <ShipmentRow key={r.id} row={r} fmtDate={fmtDate} isManager={isManager} onEdit={() => setEditing(r)} />
            ))}
          </tbody>
        </table>
      </div>

      {isManager && editing && (
        <ShipmentEditDialog
          shipment={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ShipmentRow({ row, fmtDate, isManager, onEdit }) {
  const [open, setOpen] = useState(false);
  const lines = row.lines || [];
  const multi = lines.length > 1;
  const firstLine = lines[0] || {};
  const colSpanForNotes = isManager ? 9 : 8;
  return (
    <>
      <tr
        className={`border-b border-neutral-100 hover:bg-neutral-50/60 ${multi ? "cursor-pointer" : ""}`}
        onClick={() => multi && setOpen((v) => !v)}
        data-testid={`shipment-row-${row.id}`}
      >
        <td className="px-3 py-2 font-mono text-xs">
          {multi && (
            <span className="inline-block w-3 mr-1 text-neutral-500 select-none">{open ? "▾" : "▸"}</span>
          )}
          {row.shipment_no}
        </td>
        <td className="px-3 py-2 text-xs">{fmtDate(row.received_at)}</td>
        <td className="px-3 py-2">{row.supplier_name}</td>
        <td className="px-3 py-2">
          {multi ? (
            <div>
              <div className="font-medium">{lines.length} parts</div>
              <div className="text-[10px] font-mono text-neutral-500 truncate max-w-md">
                {lines.slice(0, 3).map((l) => l.name).join(" · ")}
                {lines.length > 3 ? ` · +${lines.length - 3} more` : ""}
              </div>
            </div>
          ) : (
            <div>
              <div>{firstLine.name}</div>
              {firstLine.stock_code && (
                <div className="text-[10px] font-mono text-neutral-500">
                  {firstLine.stock_code}{firstLine.uom ? ` · ${firstLine.uom}` : ""}
                </div>
              )}
            </div>
          )}
        </td>
        <td className="px-3 py-2 text-right font-mono">{row.total_units}</td>
        <td className="px-3 py-2 text-right font-mono">
          {row.total_value != null
            ? `RM ${row.total_value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : <span className="text-neutral-400">—</span>}
        </td>
        <td className="px-3 py-2 font-mono text-xs">{row.invoice_no || <span className="text-neutral-400">—</span>}</td>
        <td className="px-3 py-2 text-xs">{row.received_by_name}</td>
        {isManager && (
          <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={onEdit}
              data-testid={`shipment-edit-${row.id}`}
              className="p-1.5 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white"
              title="Fix PO number, qty or unit cost — auto-adjusts main stock"
            >
              <PencilSimple size={13} weight="bold" />
            </button>
          </td>
        )}
      </tr>
      {multi && open && (
        <tr className="border-b border-neutral-100 bg-neutral-50/40" data-testid={`shipment-lines-${row.id}`}>
          <td colSpan={colSpanForNotes} className="px-3 py-2">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-neutral-500">
                  <th className="px-2 py-1 text-left label-eyebrow">Part</th>
                  <th className="px-2 py-1 text-left label-eyebrow">Code</th>
                  <th className="px-2 py-1 text-right label-eyebrow">Qty</th>
                  <th className="px-2 py-1 text-right label-eyebrow">Unit cost (MYR)</th>
                  <th className="px-2 py-1 text-right label-eyebrow">Line total (MYR)</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => {
                  const lt = (l.unit_cost != null && l.quantity != null) ? l.quantity * l.unit_cost : null;
                  return (
                    <tr key={`${row.id}-line-${i}`} className="border-t border-neutral-200">
                      <td className="px-2 py-1">{l.name}</td>
                      <td className="px-2 py-1 font-mono">{l.stock_code || <span className="text-neutral-400">free-text</span>}</td>
                      <td className="px-2 py-1 text-right font-mono">{l.quantity}</td>
                      <td className="px-2 py-1 text-right font-mono">{l.unit_cost != null ? `RM ${l.unit_cost.toFixed(2)}` : <span className="text-neutral-400">—</span>}</td>
                      <td className="px-2 py-1 text-right font-mono">{lt != null ? `RM ${lt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : <span className="text-neutral-400">—</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {row.notes && (
              <div className="text-[11px] text-neutral-500 mt-2">Notes: {row.notes}</div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}


// --------------------------------------------------------------------------- //
// Manager fix-up dialog — correct a wrongly-keyed PO, qty, or unit cost. Any
// quantity delta is auto-applied to main stock by the backend PATCH endpoint.
// --------------------------------------------------------------------------- //
function ShipmentEditDialog({ shipment, onClose, onSaved }) {
  const [invoiceNo, setInvoiceNo] = useState(shipment.invoice_no || "");
  const [notes, setNotes] = useState(shipment.notes || "");
  const [lines, setLines] = useState(
    (shipment.lines || []).map((l) => ({
      stock_code: l.stock_code || null,
      name: l.name || "",
      group: l.group || null,
      uom: l.uom || null,
      quantity: l.quantity || 0,
      unit_cost: l.unit_cost ?? "",
    }))
  );
  const [busy, setBusy] = useState(false);

  const setLine = (i, k, v) => setLines(lines.map((l, idx) => idx === i ? { ...l, [k]: v } : l));

  // Compute the stock delta preview so the manager can see the impact before saving.
  const stockDeltaPreview = useMemo(() => {
    const collapse = (rows) => rows.reduce((acc, l) => {
      const code = l.stock_code;
      if (!code) return acc;
      acc[code] = (acc[code] || 0) + Number(l.quantity || 0);
      return acc;
    }, {});
    const before = collapse(shipment.lines || []);
    const after = collapse(lines);
    const codes = new Set([...Object.keys(before), ...Object.keys(after)]);
    return Array.from(codes)
      .map((c) => ({ code: c, delta: (after[c] || 0) - (before[c] || 0) }))
      .filter((r) => r.delta !== 0);
  }, [shipment.lines, lines]);

  const submit = async () => {
    setBusy(true);
    try {
      const cleaned = lines.map((l) => ({
        stock_code: l.stock_code || null,
        name: (l.name || "").trim(),
        group: l.group || null,
        uom: l.uom || null,
        quantity: Math.max(1, Number(l.quantity) || 1),
        unit_cost: l.unit_cost === "" ? null : Number(l.unit_cost),
      })).filter((l) => l.name);
      if (cleaned.length === 0) return toast.error("At least one line is required");
      await api.patch(`/suppliers/shipments/${shipment.id}`, {
        invoice_no: invoiceNo.trim() || null,
        notes: notes.trim() || null,
        lines: cleaned,
      });
      toast.success(`Shipment ${shipment.shipment_no} corrected · main stock synced`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl rounded-none border-neutral-900 max-h-[90vh] overflow-y-auto" data-testid="shipment-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
            <PencilSimple size={22} weight="duotone" />
            Fix {shipment.shipment_no} · {shipment.supplier_name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="border border-amber-600 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <b>Manager correction.</b> Any qty change re-balances <b>main stock</b> automatically
            (positive delta adds back, negative deducts). Original delivery stays on record.
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">PO / Invoice number</Label>
              <Input
                value={invoiceNo}
                onChange={(e) => setInvoiceNo(e.target.value)}
                placeholder="e.g. PO-2026-0123"
                className="rounded-none border-neutral-400 font-mono"
                data-testid="edit-invoice-no"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Notes</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="rounded-none border-neutral-400"
                data-testid="edit-notes"
                placeholder="Optional"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="label-eyebrow">Lines</Label>
            <div className="hidden md:grid grid-cols-12 gap-2 px-1 text-[10px] font-mono uppercase tracking-wider text-neutral-500">
              <div className="col-span-5">Part</div>
              <div className="col-span-2 font-mono">Stock code</div>
              <div className="col-span-2 text-right">Qty</div>
              <div className="col-span-3 text-right">Unit cost (RM)</div>
            </div>
            {lines.map((l, i) => {
              const oldQty = shipment.lines[i]?.quantity ?? "—";
              return (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`edit-line-${i}`}>
                  <Input
                    value={l.name}
                    onChange={(e) => setLine(i, "name", e.target.value)}
                    className="col-span-5 rounded-none border-neutral-400 h-9"
                    data-testid={`edit-name-${i}`}
                  />
                  <Input
                    value={l.stock_code || ""}
                    onChange={(e) => setLine(i, "stock_code", e.target.value || null)}
                    placeholder="—"
                    className="col-span-2 rounded-none border-neutral-400 h-9 font-mono text-xs"
                    data-testid={`edit-code-${i}`}
                  />
                  <div className="col-span-2 relative">
                    <Input
                      type="number" min={1} value={l.quantity}
                      onChange={(e) => setLine(i, "quantity", e.target.value)}
                      className="rounded-none border-neutral-400 h-9 text-right font-mono"
                      data-testid={`edit-qty-${i}`}
                    />
                    {oldQty !== l.quantity && oldQty !== "—" && (
                      <div className="absolute -bottom-4 right-0 text-[9px] font-mono text-neutral-500">was {oldQty}</div>
                    )}
                  </div>
                  <Input
                    type="number" min={0} step="0.01"
                    value={l.unit_cost}
                    onChange={(e) => setLine(i, "unit_cost", e.target.value)}
                    className="col-span-3 rounded-none border-neutral-400 h-9 text-right font-mono"
                    data-testid={`edit-unitcost-${i}`}
                    placeholder="—"
                  />
                </div>
              );
            })}
          </div>

          {stockDeltaPreview.length > 0 && (
            <div className="border-t-2 border-amber-600 pt-3">
              <div className="label-eyebrow mb-2">Main stock will change on save</div>
              <div className="space-y-1 font-mono text-xs" data-testid="edit-delta-preview">
                {stockDeltaPreview.map((d) => (
                  <div key={d.code} className="flex justify-between">
                    <span>{d.code}</span>
                    <span className={d.delta > 0 ? "text-emerald-700 font-semibold" : "text-red-600 font-semibold"}>
                      {d.delta > 0 ? "+" : ""}{d.delta}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="edit-submit"
            className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800">
            <FloppyDisk size={14} weight="bold" className="mr-1" />
            {busy ? "Saving…" : "Save + sync stock"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function StatCard({ label, value, mono }) {
  return (
    <div className="border border-neutral-200 bg-white px-4 py-3">
      <div className="label-eyebrow">{label}</div>
      <div className={`mt-1 font-display text-2xl ${mono ? "font-mono text-xl" : ""}`}>{value}</div>
    </div>
  );
}
